import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatApiError, getStoredClient } from "../lib/api";
import ClientLogo from "../components/ClientLogo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function Login({ superadmin = false }) {
  const { login, user } = useAuth();
  const client = superadmin ? null : getStoredClient();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email.trim(), password, client?.access_code);
      if (superadmin) {
        if (u.role !== "admin") throw new Error("Accesso riservato al Superadmin");
        navigate("/superadmin", { replace: true });
      } else {
        navigate(u.role === "admin" ? "/admin" : "/responsabile", { replace: true });
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (!superadmin && user?.client_id) return <Navigate to={user.role === "admin" ? "/admin" : "/responsabile"} replace />;
  if (!superadmin && !client) return <Navigate to="/" replace />;

  return (
    <div className="relative min-h-screen flex flex-col">
      {/* Decorative background */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-25"
        style={{ backgroundImage: `url(https://images.pexels.com/photos/26885619/pexels-photo-26885619.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400)` }}
        aria-hidden
      />
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50/95 via-white/95 to-blue-50/90 backdrop-blur-xl" aria-hidden />

      <div className="relative z-10 flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md anim-fade-up">
          <div className="text-center mb-8">
            {superadmin ? (
              <img src="/brand/lmkt.png" alt="LMKT" className="h-28 w-28 mx-auto object-contain" data-testid="login-client-logo" />
            ) : (
              <ClientLogo client={client} className="h-24 w-24 mx-auto ring-2 ring-white shadow-md text-2xl" testId="login-client-logo" />
            )}
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mt-6 tracking-tight" data-testid="login-client-title">
              {superadmin ? "Dance Analysis System" : client.title}
            </h1>
            <p className="text-xs uppercase tracking-[0.25em] text-blue-700 font-semibold mt-2" data-testid="login-client-subtitle">
              {superadmin ? "Area Superadmin" : client.subtitle}
            </p>
          </div>

          <div className="bg-white rounded-2xl ring-1 ring-slate-200 shadow-sm p-7 sm:p-9">
            <h2 className="font-display text-2xl font-semibold text-slate-900 mb-1">Accedi</h2>
            <p className="text-sm text-slate-500 mb-6">Inserisci le tue credenziali per continuare</p>

            <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
              <div>
                <Label htmlFor="email" className="text-sm font-semibold text-slate-700">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-2 h-12 bg-slate-50 hover:bg-white focus:bg-white border-slate-200"
                  placeholder="nome@esempio.it"
                  data-testid="login-email-input"
                />
              </div>

              <div>
                <Label htmlFor="password" className="text-sm font-semibold text-slate-700">Password</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-2 h-12 bg-slate-50 hover:bg-white focus:bg-white border-slate-200"
                  data-testid="login-password-input"
                />
              </div>

              {error && (
                <div className="text-sm font-medium text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-4 py-3" data-testid="login-error">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-blue-700 hover:bg-blue-800 text-white font-medium rounded-lg"
                data-testid="login-submit-button"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Entra"}
              </Button>
            </form>
          </div>

          {superadmin && (
            <p className="text-center mt-6">
              <Link to="/" className="text-sm text-slate-500 hover:text-blue-700 underline-offset-4 hover:underline" data-testid="login-back-home-link">
                Torna alla pagina iniziale
              </Link>
            </p>
          )}
          <p className="text-center text-xs text-slate-400 mt-6">
            Sviluppato da <span className="font-semibold text-slate-600">LMKT</span> — Simone Arrighi &amp; Valeria Tria
          </p>
        </div>
      </div>
    </div>
  );
}
